import type { Communication } from '@medplum/fhirtypes'
import { Loader } from '@mantine/core'
import { formatDateTime } from '@medplum/core'

import InfiniteScroll from 'react-infinite-scroll-component'
import { extractNote } from './utils'
import { CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'

type Props = {
  notes: Communication[]
  selectedId: string | undefined
  hasMore: boolean
  isLoading: boolean
  onSelect: (note: Communication) => void
  onLoadMore: () => void
}

export function NotesList({ notes, selectedId, hasMore, isLoading, onSelect, onLoadMore }: Props) {
  // Pin the `fixed` left rail below the sticky `<PatientChartActionBar>` —
  // same ScrollContext bypass as `<ListLayout>`/`<ChronologicalRecords>`
  // (CallPage publishes `stickyTop="0px"`, wrong for a viewport-`fixed` rail).
  const stickyTop = CHART_CONTENT_TOP_VAR

  return (
    <div
      id="careCoordScrollable"
      className="fixed w-[280px] overflow-auto bg-[#FBFCFE]"
      style={{ top: stickyTop, height: `calc(100vh - ${stickyTop})` }}
    >
      <div className="sticky top-0 z-10 w-full bg-[#FBFCFE] pt-4" />

      {!notes.length && (
        <div className="px-4 text-center text-sm text-gray-500">
          {isLoading ? 'Loading notes...' : 'No notes available'}
        </div>
      )}

      <InfiniteScroll
        dataLength={notes.length}
        next={onLoadMore}
        hasMore={hasMore}
        loader={<Loader color="blue" />}
        scrollableTarget="careCoordScrollable"
        style={{ display: 'contents' }}
      >
        {notes.map((note) => {
          const noteText = extractNote(note)
          const author = note.sender?.display || 'Unknown'
          const date = formatDateTime(note.meta?.lastUpdated || note.sent || '')
          const preview = noteText.length > 60 ? `${noteText.slice(0, 60)}…` : noteText

          return (
            <div
              key={note.id}
              onClick={() => onSelect(note)}
              className={[
                'cursor-pointer border-b border-b-[#D0E1F2] px-4 py-2 text-[13px]',
                note.id === selectedId ? 'bg-[#dcebfd]' : 'hover:bg-[#F0F7FF]',
              ].join(' ')}
            >
              <div className="flex justify-between">
                <p className="text-primary wrap-break-words flex-1">{author}</p>
                <p className="text-[#7D7987]">{date}</p>
              </div>
              <p className="text-[#7D7987]">{preview}</p>
            </div>
          )
        })}
      </InfiniteScroll>
    </div>
  )
}
